/** Meta/WhatsApp-native operational issues (non-Twilio-transport). */
import type { IssueDefinition } from "../issue-definition";
import { ISSUE_CATEGORY, ISSUE_FAULT } from "../issue-enums";
import { META_WHATSAPP_MANAGER_CTA } from "./ctas";

export const WHATSAPP_ISSUES = {
  WHATSAPP_SENDER_WABA_CONFLICT: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "WhatsApp sender account linked to another WABA ID with more than 1 sender",
    severity: "high",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "WhatsApp number linked to another account",
    userGuidance:
      "This number is registered under a different WhatsApp Business Account that already has multiple senders. Resolve the conflict in Meta WhatsApp Manager, then retry.",
    cta: META_WHATSAPP_MANAGER_CTA
  },
  WHATSAPP_SENDER_RELEASE_FAILED: {
    // Admin-only: during a phone number release the sender was removed in Nexus,
    // but Twilio/Meta rejected the deregistration, so it may linger on Twilio.
    category: ISSUE_CATEGORY.PROVIDER,
    message:
      "WhatsApp sender removed in Nexus during phone number release, but Twilio/Meta deregistration failed — remove the sender manually in Twilio / WhatsApp Manager",
    severity: "medium",
    fault: ISSUE_FAULT.NEXUS
  },
  META_TOKEN_EXPIRED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Meta access token has expired",
    severity: "high",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "Meta connection expired",
    userGuidance:
      "Your Meta access token has expired, so WhatsApp messages can't be sent. Reconnect your WhatsApp integration to restore messaging."
  },
  WHATSAPP_TEMPLATE_REJECTED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "WhatsApp message template was rejected",
    severity: "medium",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "WhatsApp template rejected",
    userGuidance:
      "Meta rejected one of your message templates. Edit it to meet Meta's content guidelines and resubmit it in WhatsApp Manager.",
    cta: META_WHATSAPP_MANAGER_CTA
  },
  // Direct WhatsApp Cloud API send failures (Graph API, non-Twilio transport).
  META_WHATSAPP_AUTH_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "WhatsApp Cloud API access token is invalid or expired",
    severity: "critical",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "WhatsApp connection invalid",
    userGuidance:
      "Your WhatsApp connection is invalid or expired, so the agent can't send or receive WhatsApp messages. Reconnect the number to restore it.",
    cta: META_WHATSAPP_MANAGER_CTA
  },
  META_WHATSAPP_PERMISSION_DENIED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Meta denied permission for the WhatsApp send request",
    severity: "high",
    fault: ISSUE_FAULT.USER_ACTION,
    userTitle: "WhatsApp permission denied",
    userGuidance:
      "Meta refused a WhatsApp send because the app or number lacks the required permission. Reconnect the number and grant messaging access.",
    cta: META_WHATSAPP_MANAGER_CTA
  },
  META_WHATSAPP_RATE_LIMIT: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Meta WhatsApp Cloud API rate limit exceeded",
    severity: "medium",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "WhatsApp rate limit reached",
    userGuidance:
      "Meta is rate-limiting WhatsApp requests. Delivery catches up on its own once the limit clears."
  },
  META_WHATSAPP_MESSAGE_SEND_FAILED: {
    category: ISSUE_CATEGORY.PROVIDER,
    message: "Meta WhatsApp message failed to send",
    severity: "high",
    fault: ISSUE_FAULT.PROVIDER,
    userTitle: "WhatsApp message not delivered",
    userGuidance:
      "A WhatsApp message could not be delivered due to a Meta Graph API error. It may succeed on retry; persistent failures usually mean the number connection needs attention."
  }
} as const satisfies Record<string, IssueDefinition>;
